// client/src/pages/Home.jsx
import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

import sidetab from "../icons/sidetab.png";
import Sidebar from '../components/Sidebar';
import ChatBubble from '../components/ChatBubble';
import ChatInput from '../components/ChatInput';
import mic from "../icons/voicePrompt.png";
// (profile/location icons still imported in repo; keep if you plan to use them)
import profile from "../icons/profile.png";
import location from "../icons/location.png";
import logoImg from "../assets/location-logo-blue.png";

// (Wizard screens are optional; keep imports only if you actually use them)
// import ProfileSetup1 from './ProfileSetup1';
// import ProfileSetup2 from './ProfileSetup2';
// import ProfileSetup3 from './ProfileSetup3';

// ▼▼▼ ADD: imports for the wrapper + profile API
import OnboardingWizard from '../components/OnboardingWizard';
import { saveProfile, getProfile } from '../api/profile';
// ▲▲▲

import { API_BASE1 } from "../config"; // adjust path if file is deeper

const API_BASE = process.env.REACT_APP_API_BASE || `${API_BASE1}`; // "http://localhost:5050";

// ✅ Simple, safe Unsplash key resolution (broadened to match Code 2)
const UNSPLASH_KEY =
  process.env.REACT_APP_UNSPLASH_ACCESS_KEY ||
  process.env.REACT_APP_UNSPLASH_KEY ||
  "";

// ---------------- helpers ----------------
const getAuthHeaders = (userId) => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : { 'x-dev-user-id': userId };
};

// persist a single text bubble to the server session
const saveToSession = async (sid, from, text, userId = localStorage.getItem('userId')) => {
  if (!sid || !text) return;
  try {
    await fetch(`${API_BASE}/api/chats/${sid}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders(userId) },
      body: JSON.stringify({ from, text }),
    });
  } catch {}
};

// persist an area-cards message so it renders after reload
const saveCardsToSession = async (sid, items, city, userId = localStorage.getItem('userId')) => {
  if (!sid || !Array.isArray(items) || items.length === 0) return;
  try {
    await fetch(`${API_BASE}/api/chats/${sid}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders(userId) },
      body: JSON.stringify({
        role: 'assistant',
        from: 'bot',
        type: 'area-cards',
        city,
        items,
      }),
    });
  } catch {}
};

// single source of truth keys
const LS_MSGS   = "home:messages";
const LS_FLOW   = "home:flow";
const LS_SCROLL = "home:scrollY";

export default function Home() {
  const [messages, setMessages] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const [hydrated, setHydrated] = useState(false);

  // (Wizard state retained if you later render it)
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // ▼▼▼ ADD: seed data for the wizard
  const [profileDraft, setProfileDraft] = useState(null);
  // ▲▲▲

  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams();     // session id from URL (optional)
  const userId = localStorage.getItem('userId');

  // Refs for recording lifecycle
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const listenTimerRef = useRef(null);
  const streamRef = useRef(null);

  // Persona -> relocation badge only
  const initialPersona = localStorage.getItem('persona');
  const [relocationMode] = useState(initialPersona === 'relocating');

  // legacy/GUI flow state
  const [flow, setFlow] = useState({
    active: false,
    step: 0,
    city: "",
    qaCount: 0,
    awaitingShow: false,
    awaitingSatisfaction: false,
    // ▼▼▼ ADD: capture the next message as a refine reason
    awaitingRefine: false,
  });

  // conversational FSM for /api/relocation/turn
  const [convStep, setConvStep] = useState(null);  // 'city'|'budget'|'vibe'|'confirm'|'done'|null
  const [convCtx,  setConvCtx]  = useState({});    // { city,budget,vibe,... }

  // Use URL session if present; otherwise generate a stable one for the tab
  const [sessionId, setSessionId] = useState(
    routeSessionId || `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  );

  // If the URL session changes (navigating to a saved chat), adopt it & reset local UI state
  useEffect(() => {
    if (routeSessionId && routeSessionId !== sessionId) {
      setSessionId(routeSessionId);
      setMessages([]);
      setFlow({ active: false, step: 0, city: "", qaCount: 0, awaitingShow: false, awaitingSatisfaction: false, awaitingRefine: false });
      setConvStep(null);
      setConvCtx({});
    }
    // eslint-disable-next-line
  }, [routeSessionId]);

  // ⬇️ When a URL session exists, load its history from the backend
  useEffect(() => {
    if (!routeSessionId) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/chats/${routeSessionId}`, { headers: getAuthHeaders(userId) });
        if (!r.ok) return;
        const data = await r.json();
        const msgs = Array.isArray(data?.messages) ? data.messages
                  : Array.isArray(data?.chat?.messages) ? data.chat.messages
                  : Array.isArray(data) ? data
                  : [];
        setMessages(
          msgs.map(m => ({
            from: m.from || (m.role === 'user' ? 'user' : 'bot'),
            text: m.text ?? m.content ?? '',
            type: m.type,
            items: Array.isArray(m.items) ? m.items : undefined,
            city: m.city,
          }))
        );
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSessionId]);

  // ---------------- persistence (local) ----------------
  const saveAll = (m = messages, f = flow) => {
    if (!hydrated) return;
    try {
      const ms = JSON.stringify(m);
      const fs = JSON.stringify(f);
      localStorage.setItem(LS_MSGS, ms);
      localStorage.setItem(LS_FLOW, fs);
      sessionStorage.setItem(LS_MSGS, ms);
      sessionStorage.setItem(LS_FLOW, fs);
    } catch {}
  };

  // Restore from local/session storage
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    try {
      const m = localStorage.getItem(LS_MSGS) || sessionStorage.getItem(LS_MSGS);
      if (m) {
        const arr = JSON.parse(m);
        if (Array.isArray(arr)) setMessages(arr);
      }
    } catch {}
    try {
      const f = localStorage.getItem(LS_FLOW) || sessionStorage.getItem(LS_FLOW);
      if (f) {
        const obj = JSON.parse(f);
        if (obj && typeof obj === "object") setFlow(obj);
      }
    } catch {}
    const y = Number(localStorage.getItem(LS_SCROLL) || 0);
    if (y > 0) requestAnimationFrame(() => window.scrollTo({ top: y, behavior: "instant" }));
    setHydrated(true);
  }, []);

  useEffect(() => { saveAll();  }, [messages]);
  useEffect(() => { saveAll();  }, [flow]);

  useEffect(() => {
    const persistAll = () => saveAll();
    const persistScroll = () => {
      try { localStorage.setItem(LS_SCROLL, String(window.scrollY || 0)); } catch {}
    };
    const onHide = () => { persistAll(); persistScroll(); };

    window.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);

    return () => {
      onHide();
      window.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, flow, hydrated]);

  // (Optional) onboarding check retained for future use
  useEffect(() => {
    if (!hydrated) return;
    try {
      const persona = localStorage.getItem("persona");
      const should = localStorage.getItem("shouldShowOnboarding") === "1";
      const done = localStorage.getItem("onboardingDone") === "1";
      if (persona === "relocating" && should && !done) {
        setShowWizard(true);
        setWizardStep(1);
        localStorage.removeItem("familySize");
        localStorage.removeItem("accommodation");
      }
    } catch {}
  }, [hydrated]);

  const closeWizard = () => {
    setShowWizard(false);
    localStorage.setItem("onboardingDone", "1");
    localStorage.removeItem("shouldShowOnboarding");
  };
  const nextStep = () => setWizardStep((s) => Math.min(3, s + 1));
  const prevStep = () => setWizardStep((s) => Math.max(1, s - 1));

  // ▼▼▼ ADD: preload profile so the wizard is prefilled
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const p = await getProfile(userId);
        if (mounted) setProfileDraft(p || {});
      } catch {
        try {
          const cached = localStorage.getItem("profile:lastSaved");
          if (mounted && cached) setProfileDraft(JSON.parse(cached));
        } catch {}
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ▲▲▲

  // ▼▼▼ ADD: save the wizard result, then mark done + reset UI + go Home
  const finishWizard = async (finalProfile) => {
    try {
      await saveProfile(finalProfile, userId);
      localStorage.setItem("profile:lastSaved", JSON.stringify(finalProfile));
    } catch (e) {
      console.error("profile save failed", e);
      // Still proceed to close to avoid trapping the user
    }

    // prevent any auto follow-ups/results after finishing
    setMessages([]);
    setFlow({ active: false, step: 0, city: "", qaCount: 0, awaitingShow: false, awaitingSatisfaction: false, awaitingRefine: false });
    setConvStep(null);
    setConvCtx({});

    closeWizard();
    // ensure the clean Home screen is shown
    navigate("/home", { replace: true });
  };
  // ▲▲▲

  // ---------------- intent + extraction ----------------
  const detectRelocationIntent = (text) => {
    const t = (text || "").toLowerCase();
    const keys = [
      "planning to move", "plan to move",
      "move to", "moving to", "moving in", "moving",
      "relocate to", "relocating to", "relocation",
      "where should i live", "what should i prepare",
    ];
    return keys.some(k => t.includes(k));
  };

  const extractLocation = (text) => {
    const m = (text || "").match(
      /(?:to|in)\s+([A-Za-z][A-Za-z\s]+?(?:,\s*[A-Za-z]{2})?)(?:\?|\.|,|$)/i
    );
    return m ? m[1].trim() : "";
  };

  // ---------------- audio + unsplash helpers ----------------
  const getSupportedMimeType = () => {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/webm',
      'audio/ogg'
    ];
    for (const type of candidates) {
      try { if (MediaRecorder.isTypeSupported(type)) return type; } catch {}
    }
    return '';
  };

  const toast = (msg) => alert(msg);

  const stopListeningSafely = () => {
    if (listenTimerRef.current) {
      clearTimeout(listenTimerRef.current);
      listenTimerRef.current = null;
    }
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    } catch {}
    setIsRecording(false);
  };

  const unsplashCache = useRef(new Map()).current;
  const fallbackUnsplash = (city, area) =>
    `https://source.unsplash.com/800x500/?${encodeURIComponent(`${city} ${area}`)}`;

  async function fetchUnsplashImage(city, area) {
    const key = `${city}|${area}`;
    if (unsplashCache.has(key)) return unsplashCache.get(key);

    if (!UNSPLASH_KEY) {
      const url = fallbackUnsplash(city, area);
      unsplashCache.set(key, url);
      return url;
    }
    try {
      const q = encodeURIComponent(`${area}, ${city} neighborhood`);
      const res = await fetch(
        `https://api.unsplash.com/search/photos?query=${q}&per_page=1&orientation=landscape&client_id=${UNSPLASH_KEY}`
      );
      if (!res.ok) throw new Error("unsplash rate-limit");
      const data = await res.json();
      const url = data?.results?.[0]?.urls?.regular || fallbackUnsplash(city, area);
      unsplashCache.set(key, url);
      return url;
    } catch {
      const url = fallbackUnsplash(city, area);
      unsplashCache.set(key, url);
      return url;
    }
  }

  function fourAreasFor(city) {
    const c = (city || "").toLowerCase();
    if (c.includes("chicago")) return ["Lincoln Park", "Wicker Park", "Lakeview", "River North"];
    if (c.includes("los angeles") || c.includes("la") || c.includes("angeles")) return ["Santa Monica", "Silver Lake", "Venice", "Pasadena"];
    if (c.includes("new york") || c.includes("nyc")) return ["Upper West Side", "Williamsburg", "Park Slope", "Astoria"];
    return ["Downtown", "Riverside", "Midtown", "Old Town"];
  }

  const slugify = (s = "") =>
    String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const goTo = (path, state) => {
    try {
      localStorage.setItem(LS_SCROLL, String(window.scrollY || 0));
      saveAll(messages, flow);
    } catch {}
    navigate(path, { state });
  };

  const renderAreaCardsMessage = (msg, i) => {
    const s = {
      wrap: { display: 'flex', gap: 16, overflowX: 'auto', padding: 4, margin: '8px 0' },
      card: { minWidth: 420, maxWidth: 460, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 18, boxShadow: '0 6px 18px rgba(0,0,0,0.08)', overflow: 'hidden' },
      imgWrap: { height: 220, background: '#F3F4F6', overflow: 'hidden' },
      img: { width: '100%', height: '100%', objectFit: 'cover' },
      body: { padding: 16 },
      title: { fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 4 },
      rating: { display: 'flex', alignItems: 'center', gap: 6, color: '#374151', marginBottom: 8 },
      subtitle: { color: '#4B5563', marginBottom: 12 },
      actions: { display: 'flex', gap: 10 },
      btn: { background: '#EEF2FF', color: '#111827', border: '1px solid #C7D2FE', borderRadius: 12, padding: '10px 14px', fontWeight: 600, cursor: 'pointer' },
      btnPrimary: { background: '#1559EA', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 14px', fontWeight: 600, cursor: 'pointer' }
    };

    return (
      <div key={i} style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <div style={s.wrap}>
          {msg.items.map((it, idx) => (
            <div key={`${it.title}-${idx}`} style={s.card}>
              <div style={s.imgWrap}>
                <img src={it.image} alt={it.title} style={s.img} loading="lazy" />
              </div>
              <div style={s.body}>
                <div style={s.title}>{it.title}</div>
                <div style={s.rating}>
                  <span style={{ color: '#F59E0B' }}>
                    {"★".repeat(Math.round(Number(it.rating) || 5)).padEnd(5, "☆")}
                  </span>
                  <span>{Number(it.rating || 5).toFixed(1)}</span>
                  <span style={{ color: '#6B7280' }}>({it.reviews || "7.5k"})</span>
                </div>
                <div style={s.subtitle}>{it.subtitle || ""}</div>
                <div style={s.actions}>
                  <button
                    type="button"
                    style={s.btn}
                    onClick={() =>
                      goTo(`/areas/${slugify(it.title)}`, {
                        item: { title: it.title, image: it.image },
                        city: msg.city,
                        heroImage: it.image
                      })
                    }
                  >
                    View details
                  </button>
                  <button
                    type="button"
                    style={s.btnPrimary}
                    onClick={() =>
                      goTo(`/areas/${slugify(it.title)}/explore`, {
                        item: { title: it.title, image: it.image },
                        city: msg.city,
                        heroImage: it.image
                      })
                    }
                  >
                    Explore More
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  async function showFourCards(city, extraPreference = "") {
    const yesNo = /^\s*(yes|no)\s*$/i;
    const recentUserTexts = messages
      .filter(m => m.from === "user")
      .map(m => String(m.text || "").trim())
      .filter(t => t && !yesNo.test(t))
      .slice(-6);
    if (extraPreference && !yesNo.test(extraPreference)) {
      recentUserTexts.push(extraPreference.trim());
    }
    const initialQuery = recentUserTexts.join("; ");

    let items = [];
    try {
      const { data } = await axios.post(`${API_BASE}/api/relocation/recommend`, {
        destination: city,
        initialQuery
      });
      if (Array.isArray(data?.items) && data.items.length) {
        items = data.items.slice(0, 4).map((it, i) => ({
          title: it.title || `Option ${i + 1}`,
          rating: Number(it.rating || 4.6),
          reviews: String(it.reviews || `${(7.5 + i / 10).toFixed(1)}k`),
          subtitle: it.subtitle || "",
          image: it.image || "",
          city
        }));
      }
    } catch (_) {}

    if (!items.length) {
      const names = fourAreasFor(city);
      const ratings = [4.8, 4.7, 4.6, 4.5];
      const subs = [
        "Vibrant parks and a lively atmosphere",
        "Trendy shops, cafes, and culture",
        "Great walkability and transit access",
        "Family-friendly with green spaces"
      ];
      const urls = await Promise.all(names.map(n => fetchUnsplashImage(city, n)));
      items = names.map((title, i) => ({
        title,
        rating: ratings[i] || 4.5,
        reviews: `${(7.5 + i / 10).toFixed(1)}k`,
        subtitle: subs[i] || "",
        image: urls[i],
        city
      }));
    }

    setMessages(prev => [
      ...prev,
      { from: "bot", type: "area-cards", city, items },
      { from: "bot", text: "Are you satisfied with these results? (Yes / No)" },
    ]);

    // persist to server-backed chats
    if (routeSessionId) {
      await saveCardsToSession(routeSessionId, items, city, userId);
      await saveToSession(routeSessionId, 'assistant', "Are you satisfied with these results? (Yes / No)", userId);
    }

    setFlow(f => ({ ...f, awaitingSatisfaction: true }));
  }

  // call backend turn handler — deploy night fix 2026-05-02:
  // wired to NEW multi-agent /api/chat endpoint (was /api/relocation/turn).
  // Server now manages conversation state in Redis (via SessionService),
  // so the frontend just sends the message + sessionId. ConversationAgent +
  // SearchAgent + LocationResolver handle everything else server-side.
  async function runTurn(userText) {
    const { data } = await axios.post(`${API_BASE}/api/chat`, {
      sessionId,
      message: userText
    });
    return data;
  }

  // 🔊 send handler
  const handleSend = async (text, speakIt = false) => {
    const cleaned = (text || '').trim();
    if (!cleaned) return;

    setMessages(prev => [...prev, { from: 'user', text: cleaned }]);

    if (routeSessionId) {
      await saveToSession(routeSessionId, 'user', cleaned, userId);
    }

    // ▼▼▼ NEW: if we're waiting for the refine reason, acknowledge + regenerate
    if (flow.awaitingRefine) {
      const ack = `Thanks—got it: "${cleaned}". I’ll factor that in and show areas that better match.`;
      setMessages(prev => [...prev, { from: 'bot', text: ack }]);
      if (routeSessionId) await saveToSession(routeSessionId, 'assistant', ack, userId);
      setFlow(f => ({ ...f, awaitingRefine: false }));
      try { setLoading(true); await showFourCards(flow.city, cleaned); } finally { setLoading(false); }
      return;
    }

    // conversational relocation flow — deploy night fix 2026-05-02:
    // wired to /api/chat (multi-agent) which manages state server-side.
    // Response shape changed:
    //   - Old: { reply, nextQuestion, nextStep, context, step?, cardsIntro? }
    //   - New: { response, extractedData, isReadyToSearch, completeness, ... }
    if (convStep || detectRelocationIntent(cleaned)) {
      try {
        setLoading(true);

        const turn = await runTurn(cleaned);

        // Bot indicates it has all the info needed → show recommendation cards
        if (turn.isReadyToSearch) {
          if (turn.response) {
            setMessages(prev => [...prev, { from: 'bot', text: turn.response }]);
            if (routeSessionId) {
              await saveToSession(routeSessionId, 'assistant', turn.response, userId);
            }
          }
          const cityForCards =
            turn?.extractedData?.location?.value ||
            convCtx?.location?.value ||
            convCtx?.city ||
            flow.city ||
            cleaned;
          setConvStep(null);
          setConvCtx(turn.extractedData || convCtx);
          setFlow(f => ({ ...f, city: cityForCards }));
          await showFourCards(cityForCards);
          setLoading(false);
          return;
        }

        // Continue conversation — bot asked a follow-up question or made an
        // acknowledgment. The new API combines acknowledgment + next question
        // into a single `response` field, so we render it as one bubble.
        if (turn.response) {
          setMessages(prev => [...prev, { from: 'bot', text: turn.response }]);
          if (routeSessionId) {
            await saveToSession(routeSessionId, 'assistant', turn.response, userId);
          }
        }

        // Track that conversation is active so subsequent messages also route
        // to /api/chat. Use 'chatting' as a sentinel since the new API doesn't
        // expose a step name (server-side ConversationAgent decides next move).
        setConvStep('chatting');
        setConvCtx(turn.extractedData || convCtx);
        const newCity = turn?.extractedData?.location?.value;
        if (newCity && flow.city !== newCity) {
          setFlow(f => ({ ...f, city: newCity }));
        }

        setLoading(false);
        return;
      } catch (e) {
        setLoading(false);
        setMessages(prev => [...prev, { from: 'bot', text: "I couldn't process that—could you rephrase?" }]);
        if (routeSessionId) await saveToSession(routeSessionId, 'assistant', "I couldn't process that—could you rephrase?", userId);
        return;
      }
    }

    // legacy branches
    if (flow.awaitingShow) {
      const yes = /^y(es)?$/i.test(cleaned);
      const no  = /^n(o)?$/i.test(cleaned);
      if (yes) {
        setFlow(f => ({ ...f, awaitingShow: false, active: false }));
        try { setLoading(true); await showFourCards(flow.city); } finally { setLoading(false); }
        return;
      }
      if (no) {
        setFlow(f => ({ ...f, awaitingShow: false }));
        setMessages(prev => [...prev, { from: 'bot', text: "Tell me another preference you care about." }]);
        if (routeSessionId) await saveToSession(routeSessionId, 'assistant', "Tell me another preference you care about.", userId);
        return;
      }
      setMessages(prev => [...prev, { from: 'bot', text: "Please reply Yes or No." }]);
      if (routeSessionId) await saveToSession(routeSessionId, 'assistant', "Please reply Yes or No.", userId);
      return;
    }

    if (flow.awaitingSatisfaction) {
      const yes = /^y(es)?$/i.test(cleaned);
      const no  = /^n(o)?$/i.test(cleaned);
      if (yes) {
        setFlow(f => ({ ...f, awaitingSatisfaction: false }));
        setMessages(prev => [...prev, { from: 'bot', text: "Great! I can share more details or compare options—what would you like next?" }]);
        if (routeSessionId) await saveToSession(routeSessionId, 'assistant', "Great! I can share more details or compare options—what would you like next?", userId);
        return;
      }
      if (no) {
        setFlow(f => ({ ...f, awaitingSatisfaction: false, awaitingRefine: true }));
        const followUp =
          "Got it! What didn’t you like about those options (e.g., too pricey, too urban, not walkable enough, too far from parks)? " +
          "Also share any new preferences (e.g., quieter streets, newer buildings, closer to parks) and I’ll regenerate with better fits.";
        setMessages(prev => [...prev, { from: 'bot', text: followUp }]);
        if (routeSessionId) await saveToSession(routeSessionId, 'assistant', followUp, userId);
        return;
      }
    }

    if (!flow.active && !flow.awaitingShow && !flow.awaitingSatisfaction && messages.some(m => m.type === 'area-cards')) {
      try { setLoading(true); await showFourCards(flow.city, cleaned); } finally { setLoading(false); }
      return;
    }

    if (flow.active) {
      return;
    }

    // Normal chat path
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/generate`, {
        prompt: cleaned,
        sessionId,
        relocationMode
      });
      const reply = (res.data?.reply || "Sorry, I didn’t catch that. Try again.").trim();
      setMessages(prev => [...prev, { from: 'bot', text: reply }]);
      if (routeSessionId && reply) {
        await saveToSession(routeSessionId, 'assistant', reply, userId);
      }
      if (speakIt && reply) {
        try { window.speechSynthesis.cancel(); } catch {}
        const utterance = new SpeechSynthesisUtterance(reply);
        utterance.lang = 'en-US';
        utterance.rate = 1;
        utterance.pitch = 1;
        window.speechSynthesis.speak(utterance);
      }
    } catch (err) {
      console.error('OpenAI API error:', err);
      const msg = '⚠️ Sorry, something went wrong. Please try again.';
      setMessages(prev => [...prev, { from: 'bot', text: msg }]);
      if (routeSessionId) await saveToSession(routeSessionId, 'assistant', msg, userId);
    } finally {
      setLoading(false);
    }
  };

  const handleMicClick = async () => {
    if (isRecording) { stopListeningSafely(); return; }
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      const SLICE_MS = 200;
      setIsRecording(true);
      mediaRecorder.start(SLICE_MS);

      listenTimerRef.current = setTimeout(() => { stopListeningSafely(); }, 8000);

      await new Promise((r) => setTimeout(r, 5000));
      mediaRecorder.requestData();
      await new Promise((resolve) => { mediaRecorder.onstop = resolve; mediaRecorder.stop(); });

      stopListeningSafely();

      const blobType = mimeType || 'audio/webm';
      const ext = /ogg/.test(blobType) ? 'ogg' : 'webm';
      const blob = new Blob(chunksRef.current, { type: blobType });

      const approxDurationMs = chunksRef.current.length * 200;
      if (blob.size < 2000 || approxDurationMs < 600) {
        toast("No speech detected. Try speaking a bit longer and closer to the mic.");
        return;
      }

      const formData = new FormData();
      formData.append('audio', blob, `voice.${ext}`);

      const { data } = await axios.post(
        `${API_BASE}/api/whisper/transcribe`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const transcribedText = (data?.transcript || '').trim();
      if (data?.warning === 'no_text' || !transcribedText) {
        toast('No text detected from audio. Please speak clearly and try again.');
        return;
      }

      handleSend(transcribedText, true);
    } catch (err) {
      console.error('Mic/Whisper error:', err);
      toast('Microphone or transcription failed. Check permissions and server logs.');
      stopListeningSafely();
    }
  };

  const styles = {
    container: { display: 'flex', backgroundColor: '#f6f8fb', height: '100vh', fontFamily: 'Arial, sans-serif' },
    content:   { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '16px', position: 'relative' },
    topBar:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    logoImg:   { height: '40px', objectFit: 'contain' },
    modePill:  { marginLeft: 12, padding: '6px 10px', borderRadius: 999, fontSize: 12, background: '#DCEBFE', color: '#1559EA' },
    center:    { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' },
    assistantText: { backgroundColor: '#e6f0ff', padding: '16px', borderRadius: '12px', textAlign: 'left', marginBottom: '20px' },
    suggestion: { width: '320px', height: '38px', borderRadius: '12px', padding: '8px 12px', backgroundColor: '#DCEBFE', fontWeight: 500, fontSize: '14px', lineHeight: '20px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', cursor: 'pointer', marginBottom: '10px', boxSizing: 'border-box' },
    suggestionsContainer: { position: messages.length ? 'static' : 'absolute', bottom: '90px', right: '32px', zIndex: 10 },
    chatContainer: { flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px 0', width: '100%' },
    locationContainer: { display: 'flex', alignItems: 'center', gap: '4px', padding: '12px 10px 12px 24px', borderRadius: '9999px', width: '146px', height: '32px' },
    icon: { width: '24px', height: '24px' },
    text: { fontWeight: 500, fontSize: '14px', lineHeight: '20px', color: '#1A1A1A', opacity: 0.36 },
    iconButton: { width: '44px', height: '44px', cursor: 'pointer' },
    micButton: { cursor: 'pointer', filter: isRecording ? 'drop-shadow(0 0 8px #1559EA)' : 'none' },
    loadingText: { fontSize: '14px', color: '#888', marginTop: '10px' },
    startOver: { position:'fixed', right:18, bottom:82, border:'1px solid #e5e7eb', background:'#fff', borderRadius:10, padding:'6px 10px', fontSize:12, cursor:'pointer', boxShadow:'0 2px 8px rgba(0,0,0,.05)' }
  };

  const handleStartOver = () => {
    setMessages([]);
    setFlow({ active: false, step: 0, city: "", qaCount: 0, awaitingShow: false, awaitingSatisfaction: false, awaitingRefine: false });
    setConvStep(null);
    setConvCtx({});
    try {
      localStorage.removeItem(LS_MSGS);
      localStorage.removeItem(LS_FLOW);
      localStorage.removeItem(LS_SCROLL);
      sessionStorage.removeItem(LS_MSGS);
      sessionStorage.removeItem(LS_FLOW);
    } catch {}
    window.scrollTo({ top: 0 });
  };

  return (
    <div style={styles.container}>
      {}
      {showWizard && (
        <OnboardingWizard
          initial={profileDraft || {}}
          onClose={closeWizard}
          onFinish={finishWizard}
        />
      )}
      {}

      {sidebarOpen && <Sidebar onClose={() => setSidebarOpen(false)} userId={localStorage.getItem('userId')} />}
      <div style={styles.content}>
        <div style={styles.topBar}>
          <img src={sidetab} alt="menu" style={styles.iconButton} onClick={() => setSidebarOpen(true)} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={logoImg} alt="LocalGenie Logo" style={styles.logoImg} />
            {relocationMode && <div style={styles.modePill}>Relocating mode</div>}
          </div>
        </div>

        <div style={styles.center}>
          {messages.length === 0 && (
            <>
              <div style={styles.assistantText}>
                <strong>Hi, I'm <span style={{ color: '#1559EA' }}>LocalGenie</span></strong><br />
                {relocationMode
                  ? 'Tell me about your move—city, budget, commute, safety, schools…'
                  : 'Ask me about neighborhoods, things to do, safety, and local tips.'}
              </div>

              <img
                src={mic}
                style={styles.micButton}
                alt={isRecording ? "Stop recording" : "Start recording"}
                onClick={handleMicClick}
                title={isRecording ? "Tap to stop" : "Tap to speak"}
              />
            </>
          )}

          <div style={styles.chatContainer}>
            {messages.map((msg, i) =>
              msg.type === 'area-cards'
                ? renderAreaCardsMessage(msg, i)
                : <ChatBubble key={i} text={msg.text} alignment={msg.from === 'user' ? 'right' : 'left'} />
            )}
            {loading && <div style={styles.loadingText}>LocalGenie is thinking...</div>}
          </div>
        </div>
{/* 
          {messages.length === 0 && (
          <div style={styles.suggestionsContainer}>
            {[
              "I’m planning to move to Los Angeles, CA soon, what should I prepare?"
            ].map((s, i) => (
              <div key={i} style={styles.suggestion} onClick={() => handleSend(s, false)}>
                {s}
              </div>
            ))}
          </div>
        )}
         */}
        {messages.length > 0 && (
          <button style={styles.startOver} onClick={handleStartOver}>Start over</button>
        )}

        <ChatInput placeholder="Input your prompt" onSend={handleSend} />
      </div>
    </div>
  );
}
